import { Fragment, useMemo } from 'react';
import { FileTextIcon } from 'lucide-react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { createSearchParams, useNavigate } from 'react-router';

import { SummaryCard } from 'src/containers/Escrow/Summary';
import { Table, TableBody, TableCell, TableRow, TableFooter } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  agreementTemplateOptionsRichDictionary,
  legalContextMap,
  releaseEventsMap,
  jurisdictionMap,
  liabilityMap,
} from 'src/containers/Escrow/Agreement';
import { AgreementConditionEnum, LegalContextEnum, LiabilityEnum } from 'src/graphql/schema';
import { cn } from 'src/lib/utils';
import { Badge } from '@/components/ui/badge';

type IConfigurationData = {
  label: string;
  value: React.ReactNode;
};

const configColumnHelper = createColumnHelper<IConfigurationData>();

const configColumns = [
  configColumnHelper.accessor('label', {
    header: '',
    cell: (info) => <div className="font-semibold text-xs text-muted-foreground">{info.getValue()}</div>,
  }),
  configColumnHelper.accessor('value', {
    header: '',
    cell: (info) => info.getValue(),
  }),
];

const agreementTemplateDescriptionMap: Record<string, string> = {
  ['true']: 'A custom template supplied by you, used to generate the escrow agreement.',
  ['false']:
    'Our tried and tested, standard template, used to generate a tailored escrow agreement based on your selected configurations.',
};

const Configuration = () => {
  const {
    softwareEscrow: { id: softwareEscrowId, agreement, currentCompanyRole, startedBy },
  } = useSoftwareEscrow();
  const navigate = useNavigate();
  const { icon: Icon, label } = agreementTemplateOptionsRichDictionary[String(agreement.thirdPartyAgreement)];
  const description = agreementTemplateDescriptionMap[String(agreement.thirdPartyAgreement)];

  const data = useMemo(
    () => [
      {
        label: 'Agreement template',
        value: (
          <div className="flex gap-2 min-w-62.5">
            <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
              <Icon className="size-4 text-slate-700" />
            </div>
            <div className="grow space-y-1">
              <div className="font-semibold text-sm">{label}</div>
              <div className="text-xs text-muted-foreground">{description}</div>
            </div>
          </div>
        ),
      },
    ],
    [Icon, label, description],
  );

  const table = useReactTable({
    data,
    columns: configColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const onEdit = () => {
    navigate({
      pathname: `/software-escrows/onboarding/${softwareEscrowId}/agreement-setup`,
      search: createSearchParams({
        isEdit: 'true',
      }).toString(),
    });
  };

  const isInvitedParty = currentCompanyRole !== startedBy;

  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-foreground">Configuration</div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="hover:bg-white">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className={cn(cell.column.id === 'label' && 'w-47.5')}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          {!isInvitedParty && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={3} className="text-right">
                  <Button variant="secondary" size="sm" onClick={onEdit}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
};

type IDetailsData = {
  label: string;
  value: React.ReactNode;
  isTopAligned?: boolean;
};

const columnHelper = createColumnHelper<IDetailsData>();

const columns = [
  columnHelper.accessor('label', {
    header: '',
    cell: (info) => <div className="font-semibold text-xs text-muted-foreground">{info.getValue()}</div>,
  }),
  columnHelper.accessor('value', {
    header: '',
    cell: (info) => info.getValue(),
  }),
];

const Details = () => {
  const {
    softwareEscrow: { id: softwareEscrowId, agreement, currentCompanyRole, startedBy },
  } = useSoftwareEscrow();
  const navigate = useNavigate();

  const data = useMemo(() => {
    const tableData: IDetailsData[] = [];

    if (agreement.legalContext?.length) {
      tableData.push({
        label: 'Legal context',
        isTopAligned: true,
        value: (
          <Table>
            <TableBody>
              {agreement.legalContext.map((context) => {
                const { icon: Icon, title, description } = legalContextMap[context];

                return (
                  <Fragment key={context}>
                    <TableRow className="border-b-0 group/legal-context hover:bg-white">
                      <TableCell className="flex gap-2 pt-0 px-0 group-last/legal-context:pb-0">
                        <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                          <Icon className="size-4 text-slate-700" />
                        </div>
                        <div className="grow space-y-1">
                          <div className="font-semibold text-sm">{title}</div>
                          <div className="text-xs text-muted-foreground">{description}</div>
                        </div>
                      </TableCell>
                    </TableRow>
                    {context === LegalContextEnum.Other && (
                      <TableRow className="border-b-0 group/legal-context hover:bg-white">
                        <TableCell className="flex items-center gap-2 pt-0 px-0 group-last/legal-context:pb-0">
                          <div className="size-8 shrink-0" />
                          <div className="space-y-1 text-xs">
                            <div className="font-semibold">Your agreement title</div>
                            <div className="text-muted-foreground">{agreement.legalContextOther}</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        ),
      });
    }

    if (agreement.conditions?.length) {
      tableData.push({
        label: 'Release events',
        isTopAligned: true,
        value: (
          <Table>
            <TableBody>
              {agreement.conditions.map((condition) => {
                const { icon: Icon, title, description } = releaseEventsMap[condition];
                return (
                  <Fragment key={condition}>
                    <TableRow className="border-b-0 group/release-event hover:bg-white">
                      <TableCell className="flex gap-2 pt-0 px-0 group-last/release-event:pb-0 min-w-62.5">
                        <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                          <Icon className="size-4 text-slate-700" />
                        </div>
                        <div className="grow space-y-1">
                          <div className="font-semibold text-sm">{title}</div>
                          <div className="text-xs text-muted-foreground">{description}</div>
                        </div>
                      </TableCell>
                    </TableRow>
                    {condition === AgreementConditionEnum.Other && (
                      <TableRow className="border-b-0 group/release-event hover:bg-white">
                        <TableCell className="flex items-center gap-2 pt-0 px-0 group-last/release-event:pb-0">
                          <div className="size-8 shrink-0" />
                          <div className="grow space-y-1 text-xs">
                            <div className="font-semibold">Your release events</div>
                            <div className="text-muted-foreground">{agreement.conditionsOther}</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        ),
      });
    }

    if (agreement.jurisdiction) {
      tableData.push({
        label: 'Jurisdiction',
        value: jurisdictionMap[agreement.jurisdiction],
      });
    }

    if (agreement.liability) {
      tableData.push({
        label: 'Escrow liability SLA',
        value: (
          <div className="flex items-center gap-8">
            {liabilityMap[agreement.liability]}
            {agreement.liability !== LiabilityEnum.Total_0 && <Badge>Add-on</Badge>}
          </div>
        ),
      });
    }

    return tableData;
  }, [agreement]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Only show details if the agreement is not a third party agreement
  if (agreement.thirdPartyAgreement) return null;

  const onEdit = () =>
    navigate({
      pathname: `/software-escrows/onboarding/${softwareEscrowId}/agreement-details`,
      search: createSearchParams({
        isEdit: 'true',
      }).toString(),
    });

  const isInvitedParty = currentCompanyRole !== startedBy;

  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-foreground">Details</div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="border-0 hover:bg-white">
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(cell.column.id === 'label' && 'w-47.5', row.original.isTopAligned && 'align-top')}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          {!isInvitedParty && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={3}>
                  <div className="flex justify-end">
                    <Button variant="secondary" size="sm" onClick={onEdit}>
                      Edit
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
};

const AgreementSettingsCard = () => {
  const {
    softwareEscrow: { currentCompanyRole, startedBy },
  } = useSoftwareEscrow();

  const isInvitedParty = currentCompanyRole !== startedBy;

  return (
    <SummaryCard
      icon={FileTextIcon}
      title="Agreement settings"
      description="The key terms and legal structure of your escrow agreement."
      note={isInvitedParty && 'Non-editable'}
    >
      <div className="space-y-5">
        <Configuration />
        <Details />
      </div>
    </SummaryCard>
  );
};

export default AgreementSettingsCard;
